<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    <h5 class="title is-5">{{ $t("devices.create") }}</h5>
    <div class="content-box">
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors }">
        <div class="field">
          <label class="label">{{ $t("devices.deviceType") }} *</label>
          <div class="control">
            <div class="select">
              <Field
                name="deviceType"
                as="select"
                :class="{ 'is-danger': errors.deviceType }"
                @change="changeSchema()"
                v-model="deviceType"
              >
                <option value="TrailCamera">TrailCamera</option>
              </Field>
            </div>
            <span class="help is-danger">{{ errors.deviceType }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("devices.deviceId") }} *</label>
          <div class="control">
            <Field
              name="deviceId"
              class="input"
              type="text"
              :placeholder="placeholder"
              maxlength="5"
              required="required"
              :class="{ 'is-danger': errors.deviceId }"
            />
            <span v-if="existsMessage" class="help is-danger">{{
              existsMessage
            }}</span>
            <span class="help is-danger">{{ errors.deviceId }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("devices.deviceLocate") }} *</label>
          <div class="control">
            <Field
              name="deviceLocate"
              class="input"
              type="text"
              placeholder="Your device location"
              maxlength="60"
              required="required"
              :class="{ 'is-danger': errors.deviceLocate }"
            />
            <span class="help is-danger">{{ errors.deviceLocate }}</span>
          </div>
        </div>

        <div class="field" v-if="deviceType === 'TrailCamera'">
          <label class="label">
            <div>
              {{ $t("devices.aiLevel") }}
              <span
                class="icon"
                style="cursor: pointer;"
                @click="openAiLevelHelpModal"
              >
                <i class="fas fa-question-circle"></i>
              </span>
            </div>
          </label>
          <div class="select">
            <Field name="aiLevel" as="select" v-model="aiLevel">
              <option value="High">{{ $t("devices.highAILevel") }}</option>
              <option value="Mid">{{ $t("devices.midAILevel") }}</option>
              <option value="Low">{{ $t("devices.lowAILevel") }}</option>
            </Field>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("devices.address") }}</label>
          <div class="control">
            <Field
              name="address"
              class="input"
              type="text"
              placeholder="Your device address"
              :class="{ 'is-danger': errors.address }"
            />
            <span class="help is-danger">{{ errors.address }}</span>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("Note") }}</label>
          <div class="control">
            <Field
              name="note"
              class="input"
              type="text"
              :class="{ 'is-danger': errors.note }"
            />
            <span class="help is-danger">{{ errors.note }}</span>
          </div>
        </div>

        <router-link
          :to="{
            name: 'devicesList',
            params: { projectId: $route.params.projectId },
          }"
          class="button has-tooltip-arrow"
          :data-tooltip="$t('Back')"
        >
          Cancel
        </router-link>

        <button
          type="submit"
          class="button ml-3 is-primary"
          v-bind:class="{ 'is-loading': isLoad }"
        >
          {{ $t("Create") }}
        </button>
      </Form>
    </div>
  </div>
  <AiLevelHelpJa
    v-if="lang === 'ja'"
    :isHelpModalOpen="aiLevelHelpModal"
    @close="aiLevelHelpModal = false"
  />
  <AiLevelHelpEnus
    v-if="lang === 'en-US'"
    :isHelpModalOpen="aiLevelHelpModal"
    @close="aiLevelHelpModal = false"
  />
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb";
import AiLevelHelpEnus from "@/components/devices/AiLevelHelpEnus.vue";
import AiLevelHelpJa from "@/components/devices/AiLevelHelpJa.vue";
import imsr from "@/imsr-api";
import { computed, ref } from "@vue/reactivity";
import { Field, Form } from "vee-validate";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import * as Yup from "yup";

export default {
  components: {
    Form,
    Field,
    Breadcrumb,
    AiLevelHelpJa,
    AiLevelHelpEnus,
  },
  setup() {
    const { t } = useI18n();
    const { locale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const isLoad = ref(false);
    const existsMessage = ref("");
    const deviceType = ref("TrailCamera");
    const placeholder = ref("ex) 00000");
    const aiLevel = ref("Mid");
    const maxLength = ref(10);
    const aiLevelHelpModal = ref(false);
    const breadcrumb = ref([
      {
        title: "Home",
        path: {
          name: "dashboard",
          params: { projectId: route.params.projectId },
        },
      },
      {
        title: "Device Config",
        path: {
          name: "devicesList",
          params: { projectId: route.params.projectId },
        },
      },
      { title: "Create Device", path: "#", isActive: true },
    ]);

    const schema = ref(
      Yup.object().shape({
        deviceId: Yup.string()
          .required(t("validate.required"))
          .matches(/^[a-zA-Z0-9]{5}$/, t("validate.alphanumeric")),
        deviceType: Yup.string().required(t("validate.required")),
        deviceLocate: Yup.string().required(t("validate.required")),
        aiLevel: Yup.string()
          .required(t("validate.required"))
          .default("Mid"),
      })
    );

    const changeSchema = () => {
      if (deviceType.value == "ThermoCamera") {
        schema.value = Yup.object().shape({
          deviceId: Yup.string()
            .required(t("validate.required"))
            .length(10, t("validate.thermoCamera")),
          deviceType: Yup.string().required(t("validate.required")),
          deviceLocate: Yup.string().required(t("validate.required")),
        });
        maxLength.value = 10;
        placeholder.value = "ex) 0000000000";
      } else {
        schema.value = Yup.object().shape({
          deviceId: Yup.string()
            .required(t("validate.required"))
            .length(5, t("validate.trailCamera")),
          deviceType: Yup.string().required(t("validate.required")),
          deviceLocate: Yup.string().required(t("validate.required")),
        });
        maxLength.value = 5;
        placeholder.value = "ex) 00000";
      }
    };

    const onSubmit = async (values) => {
      try {
        isLoad.value = true;
        values.tenantId = store.getters.tenantId;
        values.projectId = route.params.projectId;
        const exists = await imsr
          .imsrGetApi("/devices/exists", { deviceId: values.deviceId })
          .then((response) => {
            return response.exists;
          });
        if (exists) {
          existsMessage.value = t("validate.unique");
        } else {
          await imsr.imsrPostApi("/devices", values);
          router.push({
            name: "devicesList",
            params: { projectId: route.params.projectId },
          });
        }
        isLoad.value = false;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const openAiLevelHelpModal = () => {
      aiLevelHelpModal.value = true;
    };

    const closeAiLevelHelpModal = () => {
      aiLevelHelpModal.value = false;
    };

    return {
      schema,
      onSubmit,
      isLoad,
      existsMessage,
      breadcrumb,
      deviceType,
      changeSchema,
      placeholder,
      maxLength,
      aiLevel,
      aiLevelHelpModal,
      openAiLevelHelpModal,
      closeAiLevelHelpModal,
      lang: computed(() => {
        return locale.value;
      }),
    };
  },
};
</script>
